import React, { useCallback, useEffect, useState } from 'react'
import { useWebMidi } from '@/hooks/useWebMidi'
import { VIRTUAL_KEYBOARD_INPUT_ID } from '@/stores/midiStore'

interface PianoKeyboardProps {
  /** Starting octave (default 3 → C3). */
  startOctave?: number
  /** Number of octaves to render (default 2). */
  octaves?: number
  /** MIDI channel to send on (1-16, default 1). */
  channel?: number
  /**
   * Inclusive lower bound for note-on velocity (1-127, default 100).
   * Each key press picks a velocity uniformly in [velocityMin, velocityMax]
   * so the player can dial in either a fixed value (min === max) or a
   * humanized range. Note-off is always sent with velocity 0 per MIDI
   * convention.
   */
  velocityMin?: number
  /** Inclusive upper bound for note-on velocity (1-127, default 100). */
  velocityMax?: number
  /** Visual size of the keys. `lg` is sized for performance use on the dedicated /midi page. */
  size?: 'sm' | 'lg'
  /** Show octave labels on the C keys to make navigation easier. */
  showLabels?: boolean
}

const SIZE_DIMENSIONS = {
  sm: {
    whiteKeyWidth: 28,
    whiteKeyHeight: 100,
    blackKeyWidth: 18,
    blackKeyHeight: 62,
  },
  lg: {
    whiteKeyWidth: 44,
    whiteKeyHeight: 180,
    blackKeyWidth: 28,
    blackKeyHeight: 110,
  },
} as const

/** Map computer keys to semitone offsets from the current octave's C. */
const KEY_MAP: Record<string, number> = {
  a: 0, // C
  w: 1, // C#
  s: 2, // D
  e: 3, // D#
  d: 4, // E
  f: 5, // F
  t: 6, // F#
  g: 7, // G
  y: 8, // G#
  h: 9, // A
  u: 10, // A#
  j: 11, // B
  k: 12, // C+1
}

const WHITE_KEY_INDICES = [0, 2, 4, 5, 7, 9, 11] // C D E F G A B
const BLACK_KEY_INDICES = [1, 3, 6, 8, 10] // C# D# F# G# A#

// Horizontal position of each black key relative to the white key block,
// expressed as a fraction of white-key width.
const BLACK_KEY_OFFSETS: Record<number, number> = {
  1: 0.65, // C# sits between C and D
  3: 1.75, // D# sits between D and E
  6: 3.6, // F# sits between F and G
  8: 4.65, // G# sits between G and A
  10: 5.75, // A# sits between A and B
}

function noteOnBytes(
  note: number,
  velocity: number,
  channel: number,
): number[] {
  return [0x90 | (channel - 1), note, velocity]
}

function noteOffBytes(note: number, channel: number): number[] {
  return [0x80 | (channel - 1), note, 0]
}

const PianoKeyboard: React.FC<PianoKeyboardProps> = ({
  startOctave = 3,
  octaves = 2,
  channel = 1,
  velocityMin = 100,
  velocityMax = 100,
  size = 'sm',
  showLabels = false,
}) => {
  const { dispatchVirtualMidi } = useWebMidi()
  const [activeNotes, setActiveNotes] = useState<Set<number>>(new Set())

  const play = useCallback(
    (note: number) => {
      setActiveNotes(prev => {
        if (prev.has(note)) return prev
        // Sample velocity uniformly in [min, max], clamped to the
        // valid MIDI range. Defensive clamping in case the parent
        // hasn't validated its own state yet.
        const lo = Math.max(
          1,
          Math.min(127, Math.min(velocityMin, velocityMax)),
        )
        const hi = Math.max(
          1,
          Math.min(127, Math.max(velocityMin, velocityMax)),
        )
        const velocity =
          lo === hi ? lo : lo + Math.floor(Math.random() * (hi - lo + 1))
        dispatchVirtualMidi(
          VIRTUAL_KEYBOARD_INPUT_ID,
          noteOnBytes(note, velocity, channel),
        )
        return new Set(prev).add(note)
      })
    },
    [channel, dispatchVirtualMidi, velocityMin, velocityMax],
  )

  const stop = useCallback(
    (note: number) => {
      setActiveNotes(prev => {
        if (!prev.has(note)) return prev
        dispatchVirtualMidi(
          VIRTUAL_KEYBOARD_INPUT_ID,
          noteOffBytes(note, channel),
        )
        const next = new Set(prev)
        next.delete(note)
        return next
      })
    },
    [channel, dispatchVirtualMidi],
  )

  // Computer keyboard support
  useEffect(() => {
    const baseNote = (startOctave + 1) * 12 // MIDI note number for C at startOctave

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.repeat) return
      const offset = KEY_MAP[e.key.toLowerCase()]
      if (offset === undefined) return
      e.preventDefault()
      play(baseNote + offset)
    }

    const handleKeyUp = (e: KeyboardEvent) => {
      const offset = KEY_MAP[e.key.toLowerCase()]
      if (offset === undefined) return
      e.preventDefault()
      stop(baseNote + offset)
    }

    window.addEventListener('keydown', handleKeyDown)
    window.addEventListener('keyup', handleKeyUp)
    return () => {
      window.removeEventListener('keydown', handleKeyDown)
      window.removeEventListener('keyup', handleKeyUp)
    }
  }, [startOctave, play, stop])

  // Build the full list of notes to render.
  const baseNote = (startOctave + 1) * 12
  const totalSemitones = octaves * 12 + 1 // include final C

  const whiteKeys: number[] = []
  const blackKeys: { note: number; octaveOffset: number; semitone: number }[] =
    []

  for (let i = 0; i < totalSemitones; i++) {
    const semitone = i % 12
    const octaveOffset = Math.floor(i / 12)
    if (WHITE_KEY_INDICES.includes(semitone)) {
      whiteKeys.push(baseNote + i)
    }
    if (BLACK_KEY_INDICES.includes(semitone) && i < totalSemitones - 1) {
      blackKeys.push({ note: baseNote + i, octaveOffset, semitone })
    }
  }

  const { whiteKeyWidth, whiteKeyHeight, blackKeyWidth, blackKeyHeight } =
    SIZE_DIMENSIONS[size]
  const totalWidth = whiteKeys.length * whiteKeyWidth
  const labelClass =
    size === 'lg' ? 'text-[11px] text-gray-400' : 'text-[9px] text-gray-400'

  return (
    <div
      className="relative select-none touch-none"
      style={{ width: totalWidth, height: whiteKeyHeight }}
    >
      {/* White keys */}
      {whiteKeys.map((note, i) => {
        const isC = note % 12 === 0
        return (
          <button
            key={note}
            type="button"
            onPointerDown={e => {
              e.preventDefault()
              ;(e.target as HTMLElement).setPointerCapture(e.pointerId)
              play(note)
            }}
            onPointerUp={() => stop(note)}
            onPointerLeave={() => {
              if (activeNotes.has(note)) stop(note)
            }}
            className="absolute border border-gray-300 dark:border-gray-600 rounded-b transition-colors flex items-end justify-center pb-1"
            style={{
              left: i * whiteKeyWidth,
              top: 0,
              width: whiteKeyWidth,
              height: whiteKeyHeight,
              backgroundColor: activeNotes.has(note) ? '#4ECDC4' : '#ffffff',
              zIndex: 1,
            }}
          >
            {showLabels && isC && (
              <span className={labelClass}>C{note / 12 - 1}</span>
            )}
          </button>
        )
      })}

      {/* Black keys */}
      {blackKeys.map(({ note, octaveOffset, semitone }) => {
        const offset = BLACK_KEY_OFFSETS[semitone] ?? 0
        const left = (octaveOffset * 7 + offset) * whiteKeyWidth
        return (
          <button
            key={note}
            type="button"
            onPointerDown={e => {
              e.preventDefault()
              ;(e.target as HTMLElement).setPointerCapture(e.pointerId)
              play(note)
            }}
            onPointerUp={() => stop(note)}
            onPointerLeave={() => {
              if (activeNotes.has(note)) stop(note)
            }}
            className="absolute rounded-b transition-colors"
            style={{
              left,
              top: 0,
              width: blackKeyWidth,
              height: blackKeyHeight,
              backgroundColor: activeNotes.has(note) ? '#4ECDC4' : '#1f2937',
              zIndex: 2,
            }}
          />
        )
      })}
    </div>
  )
}

export default PianoKeyboard
